import { d as debounce } from './debounce-CFN_LA0O.js';

class CompareProducts extends HTMLElement {
  constructor() {
    super();
    this.productCards = this.querySelectorAll('[data-compare-product-cards]');
    //Set start height to prevent jumping
    this.CompareProductsWrapper = this.querySelector('[data-compare-product]');
    this.compareProductsWrapperHeight = this.CompareProductsWrapper.offsetHeight;
    this.CompareProductsWrapper.style.minHeight = `${this.compareProductsWrapperHeight}px`;

    // Find first sticky CTA element to determine offset
    this.stickyCTA = this.querySelector('[data-sticky-cta]');
    this.stickyMargin = this.stickyCTA ? this.stickyCTA.offsetHeight : 100;

    // Initialize properties
    this.isInitialized = false;
    this.lastScrollY = window.scrollY;
    this.isScrollingUp = false;
    this.scrollLeft = 0;
    this.isFixed = false;

    // Store container elements with data-compare-product-cards attribute
    this.productCardsContainers = this.querySelectorAll('[data-compare-product-cards]');

    // Bind methods to maintain 'this' context
    this.handleResize = this.handleResize.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.updateObserverMargin = this.updateObserverMargin.bind(this);
    this.handleScrollDirection = this.handleScrollDirection.bind(this);
    this.handleHorizontalScroll = this.handleHorizontalScroll.bind(this);

    // Initial observer creation
    this.updateObserverMargin();
  }

  connectedCallback() {
    if (this.productCards.length === 0) return;

    // Initialize on load
    this.initialize();

    // Add event listeners
    window.addEventListener('resize', debounce(this.handleResize, 100));
    window.addEventListener('scroll', this.handleScrollDirection, { passive: true });

    // Listen for horizontal scroll events
    this.parentElement.addEventListener('scroll', this.handleHorizontalScroll, {
      passive: true,
    });

    // Mark as initialized
    this.isInitialized = true;
  }

  disconnectedCallback() {
    // Clean up event listeners and observer when component is removed
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScrollDirection);
    this.parentElement.removeEventListener('scroll', this.handleHorizontalScroll);
    this.observer.disconnect();
  }

  initialize() {
    // Start observing the wrapper element
    this.observer.observe(this.CompareProductsWrapper);

    // Capture any existing horizontal scroll at initialization
    if (this.parentElement) {
      this.scrollLeft = this.parentElement.scrollLeft || 0;
    }
  }

  handleScrollDirection() {
    const currentScrollY = window.scrollY;
    const previousScrollDirection = this.isScrollingUp;

    // Determine scroll direction
    this.isScrollingUp = currentScrollY < this.lastScrollY;
    this.lastScrollY = currentScrollY;

    // Only update observer if scroll direction changed
    if (previousScrollDirection !== this.isScrollingUp) {
      this.updateObserverMargin();
    }
  }

  handleHorizontalScroll(event) {
    // Store the current horizontal scroll position
    const currentScrollLeft = event.target.scrollLeft || 0;

    // Only track horizontal scroll if we're not fixed yet
    if (!this.isFixed) {
      this.scrollLeft = currentScrollLeft;
    }

    // If we are fixed, update the position of all cards
    if (this.isFixed) {
      this.syncHorizontalScroll(currentScrollLeft);
    }
  }

  syncHorizontalScroll(scrollLeftValue) {
    // Use provided value or fallback to stored value
    const scrollLeft = scrollLeftValue !== undefined ? scrollLeftValue : this.scrollLeft;

    // Apply the horizontal scroll position to the container elements with data-compare-product-cards
    this.productCardsContainers.forEach((container) => {
      if (this.isFixed) {
        container.style.transform = `translateX(-${scrollLeft}px)`;
      }
    });
  }

  updateObserverMargin() {
    // Update the sticky margin based on current CTA height
    if (this.stickyCTA) {
      this.stickyMargin = this.stickyCTA.offsetHeight;
    }

    // Get header height from CSS variable
    const headerHeight = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0'
    );

    // Add header height to margin when scrolling up to account for the header obscuring content
    const topMargin = this.isScrollingUp ? this.stickyMargin + headerHeight : this.stickyMargin;

    // Disconnect and recreate observer with updated margin
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(this.handleIntersection, {
      threshold: 0,
      rootMargin: `-${topMargin}px 0px 0px 0px`,
    });

    // Start observing again if already initialized
    if (this.isInitialized) {
      this.observer.observe(this.CompareProductsWrapper);
    }
  }

  handleResize() {
    // Properly update the height of the compare products wrapper
    // First, temporarily remove the fixed height to get true content height
    this.CompareProductsWrapper.style.minHeight = 'auto';

    // Get the actual content height
    this.compareProductsWrapperHeight = this.CompareProductsWrapper.offsetHeight;

    // Apply the updated height
    this.CompareProductsWrapper.style.minHeight = `${this.compareProductsWrapperHeight}px`;

    // Update observer margin on resize
    this.updateObserverMargin();

    // Check if the element should still be fixed after resize
    if (this.isFixed) {
      // Get the current position of the wrapper element
      const wrapperRect = this.CompareProductsWrapper.getBoundingClientRect();

      // Get header height from CSS variable
      const headerHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0'
      );

      // Calculate the position where we consider the element to be out of view
      // This should match the logic used by the IntersectionObserver's rootMargin
      const topThreshold = this.isScrollingUp
        ? this.stickyMargin + headerHeight
        : this.stickyMargin;

      // Check if the element is still outside the viewport (should be fixed)
      const stillShouldBeFixed = wrapperRect.top + topThreshold <= 0;

      // Update fixed state if it's changed
      if (this.isFixed !== stillShouldBeFixed) {
        this.isFixed = stillShouldBeFixed;

        // Update classes on product cards based on new fixed state
        this.productCards.forEach((card) => {
          if (stillShouldBeFixed) {
            card.classList.add(
              'fixed',
              'z-fixed',
              'w-full',
              'top-[calc(var(--header-height)_-_var(--header-top))]',
              'mt-4',
              'max-w-[calc(var(--container-site)_-_var(--padding-site)_*_2)]'
            );
          } else {
            card.classList.remove(
              'fixed',
              'z-fixed',
              'w-full',
              'top-[calc(var(--header-height)_-_var(--header-top))]',
              'mt-4',
              'max-w-[calc(var(--container-site)_-_var(--padding-site)_*_2)]'
            );

            // Reset transform when returning to normal flow
            this.productCardsContainers.forEach((container) => {
              container.style.transform = '';
            });
          }
        });
      }

      // Sync horizontal scroll position if still fixed
      if (this.isFixed && this.parentElement) {
        this.syncHorizontalScroll(this.parentElement.scrollLeft);
      }
    }
  }

  handleIntersection(entries) {
    // We're only observing one element, so we can use the first entry
    const entry = entries[0];

    // If the wrapper is not intersecting (not in viewport), make cards fixed
    const shouldBeFixed = !entry.isIntersecting;

    // Check if state is changing
    const isStateChanging = this.isFixed !== shouldBeFixed;

    // Update fixed state
    this.isFixed = shouldBeFixed;

    // If becoming fixed, capture the current scroll position first
    if (isStateChanging && shouldBeFixed && this.parentElement) {
      this.scrollLeft = this.parentElement.scrollLeft || 0;
    }

    // If not fixed (or returning to non-fixed), update the wrapper height
    // This ensures the wrapper height is always correct when not fixed
    if (!shouldBeFixed) {
      // First, temporarily remove the fixed height to get true content height
      this.CompareProductsWrapper.style.minHeight = 'auto';

      // Get the actual content height
      this.compareProductsWrapperHeight = this.CompareProductsWrapper.offsetHeight;

      // Apply the updated height
      this.CompareProductsWrapper.style.minHeight = `${this.compareProductsWrapperHeight}px`;
    }

    this.productCards.forEach((card) => {
      // Toggle fixed positioning
      if (shouldBeFixed) {
        card.classList.add(
          'fixed',
          'z-fixed',
          'w-full',
          'top-[calc(var(--header-height)_-_var(--header-top))]',
          'mt-4',
          'max-w-[calc(100dvw_-_var(--padding-site)_*_2)]',
          '2xl:max-w-[calc(var(--container-site)_-_var(--padding-site)_*_2)]'
        );
      } else {
        card.classList.remove(
          'fixed',
          'z-fixed',
          'w-full',
          'top-[calc(var(--header-height)_-_var(--header-top))]',
          'mt-4',
          '2xl:max-w-[calc(var(--container-site)_-_var(--padding-site)_*_2)]',
          'max-w-[calc(100dvw_-_var(--padding-site)_*_2)]'
        );
      }
    });

    // Apply or remove transform for horizontal scroll on container elements
    const currentScrollLeft = this.parentElement ? this.parentElement.scrollLeft : this.scrollLeft;
    this.productCardsContainers.forEach((container) => {
      if (shouldBeFixed) {
        // Apply current horizontal scroll position when becoming fixed
        container.style.transform = `translateX(-${currentScrollLeft}px)`;
      } else {
        // Reset transform when returning to normal flow
        container.style.transform = '';
      }
    });
  }
}

customElements.define('compare-products', CompareProducts);
